// Build-deck page. Big textarea in, streaming agent output below.
// On successful save the page redirects to /decks/:slug — the inline
// result section only renders if the user comes back to a still-running
// build (PR 2 resume flow) before the terminal deck_saved lands.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, Send, X } from "lucide-react";
import InfoTooltip from "../components/InfoTooltip";
import {
  createBuild,
  getCardMetaBatch,
  streamBuild,
} from "../lib/api";
import type { AgentEvent, ExemplarMode } from "../lib/types";
import { useRefreshBalance } from "../lib/useBalance";
import { useMyUsername } from "../lib/useMyUsername";
import { useSubscription } from "../lib/useSubscription";
import { ACTION_COSTS } from "../lib/credits";
import StreamingOutput from "../components/StreamingOutput";
import StreamingDeckResult from "../components/StreamingDeckResult";
import LiquidButton from "../components/ui/LiquidButton";
import AskUserCard from "../components/AskUserCard";
import CollectionPicker from "../components/CollectionPicker";
import { parseProgressive } from "../lib/progressive-sections";
import { getFinalBuildAnswer } from "../lib/build-output";
import { cn } from "../lib/cn";
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGES,
  type AttachedImage,
  disposeAttachment,
  processFile,
} from "../lib/image-attachments";

const PROGRESSIVE_DISCOVERY_LS_KEY = "mtg-agent:progressive-discovery";

function readPdPref(): boolean {
  try {
    return localStorage.getItem(PROGRESSIVE_DISCOVERY_LS_KEY) === "on";
  } catch {
    return false;
  }
}
function writePdPref(value: boolean): void {
  try {
    localStorage.setItem(PROGRESSIVE_DISCOVERY_LS_KEY, value ? "on" : "off");
  } catch {
    /* ignore (private mode) */
  }
}

const SAMPLE_BRIEFS = [
  "Dimir midrange with a graveyard plan, beats control",
  "Build me the best deck you can in current Standard",
  "Selesnya tokens with populate, favor cards from the last 6 months",
  "Mono-red aggro under $40",
];

export default function BuildDeck() {
  const navigate = useNavigate();
  const refreshBalance = useRefreshBalance();
  const queryClient = useQueryClient();
  const myUsername = useMyUsername();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBrief = searchParams.get("brief") ?? "";
  const resumeBuildId = searchParams.get("resume");
  const [brief, setBrief] = useState(initialBrief);
  useEffect(() => {
    if (searchParams.has("brief")) {
      const next = new URLSearchParams(searchParams);
      next.delete("brief");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [exemplarMode, setExemplarMode] = useState<ExemplarMode>("auto");
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [progressiveDiscovery, setProgressiveDiscovery] = useState<boolean>(
    () => readPdPref(),
  );
  // Subscriber-only model preference. Defaults to Sonnet for everyone;
  // active Polar subscribers can flip to Opus per build via the toggle
  // rendered below. Free users never see the control.
  const [modelPreference, setModelPreference] = useState<"sonnet" | "opus">(
    "sonnet",
  );
  const { isSubscriber } = useSubscription();
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [lastText, setLastText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [activeBuildId, setActiveBuildId] = useState<string | null>(null);
  // Populated when deck_saved lands. Holds the slug so the page can offer a
  // "View saved deck" link instead of yanking the user off the streaming
  // result view the instant the save completes.
  const [savedDeckId, setSavedDeckId] = useState<string | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<{
    tool_use_id: string;
    question: string;
    context?: string;
    options?: string[];
  } | null>(null);
  const disposeRef = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Scroll to the accepted final deck once per build.
  const finalResponseRef = useRef<HTMLDivElement | null>(null);
  const didScrollToFinalRef = useRef(false);

  // Inspiration images. Processed (downscaled, JPEG-encoded) on the
  // client before submit so the JSON body stays small and the server's
  // 2MB-per-image cap is never an accident. Thumbnails are object URLs
  // backed by the encoded blob — disposed on remove or unmount.
  const [images, setImages] = useState<AttachedImage[]>([]);
  const [imageError, setImageError] = useState<string | null>(null);

  const canSubmit = brief.trim().length > 0 && !running;

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setImageError(null);
      // Use the latest count from state to enforce the cap correctly
      // when multiple addFiles calls land back-to-back (paste + drop).
      let next: AttachedImage[] = [];
      setImages((prev) => {
        next = prev;
        return prev;
      });
      for (const file of list) {
        if (next.length >= MAX_IMAGES) {
          setImageError(`max ${MAX_IMAGES} images`);
          break;
        }
        try {
          const att = await processFile(file);
          next = [...next, att];
          setImages(next);
        } catch (e) {
          setImageError((e as Error).message);
        }
      }
    },
    [],
  );

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) disposeAttachment(target);
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  // Paste handler: catches Cmd-V of an image from the clipboard. Only
  // handles items the browser identifies as image files; text paste
  // falls through to the textarea's default behavior.
  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        void addFiles(files);
      }
    },
    [addFiles],
  );

  // Revoke any lingering object URLs when leaving the page so we
  // don't strand blobs in browser memory.
  useEffect(() => {
    return () => {
      setImages((prev) => {
        prev.forEach(disposeAttachment);
        return [];
      });
    };
  }, []);

  // Draft text can contain a complete deck and still fail validation or need
  // more tools. Only the accepted final payload may reveal the result panel.
  // Deriving it from events also handles persisted replay on resume.
  const finalAnswer = useMemo(() => getFinalBuildAnswer(events), [events]);
  const progressive = useMemo(() => parseProgressive(finalAnswer ?? ""), [finalAnswer]);
  const cardNames = useMemo(() => {
    if (!progressive.deck) return [] as string[];
    const set = new Set<string>();
    for (const e of progressive.deck.mainboard) set.add(e.name);
    for (const e of progressive.deck.sideboard) set.add(e.name);
    return [...set];
  }, [progressive.deck]);
  const streamMetaQuery = useQuery({
    queryKey: ["card-meta-batch", cardNames],
    queryFn: () => getCardMetaBatch(cardNames),
    enabled: cardNames.length > 0,
  });

  const reset = () => {
    setEvents([]);
    setLastText("");
    setError(null);
    setPendingQuestion(null);
    setSavedDeckId(null);
    // New run gets a fresh scroll opportunity.
    didScrollToFinalRef.current = false;
  };

  // Subscribe to a build by id. Used both for freshly-created builds
  // and for ?resume=<id> deep links from the In-progress list.
  const subscribe = useCallback(
    (buildId: string) => {
      disposeRef.current?.();
      reset();
      setActiveBuildId(buildId);
      setRunning(true);
      const dispose = streamBuild(buildId, (msg) => {
        if (msg.kind === "event") {
          setEvents((prev) => [...prev, msg.data]);
          if (msg.data.type === "assistant_text_delta") {
            const delta = msg.data.text;
            setLastText((prev) => prev + delta);
          } else if (msg.data.type === "turn_start") {
            setLastText("");
          } else if (msg.data.type === "assistant_text") {
            if (msg.data.text.length > 0) setLastText(msg.data.text);
          } else if (msg.data.type === "awaiting_input") {
            setPendingQuestion({
              tool_use_id: msg.data.tool_use_id,
              question: msg.data.question,
              context: msg.data.context,
              options: msg.data.options,
            });
            queryClient.invalidateQueries({ queryKey: ["builds", "in-progress"] });
          } else if (msg.data.type === "user_response") {
            // Another tab answered the question, or our own POST just
            // landed. Clear the pending prompt either way.
            setPendingQuestion(null);
          }
        } else if (msg.kind === "deck_saved") {
          setRunning(false);
          refreshBalance();
          queryClient.invalidateQueries({ queryKey: ["builds", "in-progress"] });
          queryClient.invalidateQueries({ queryKey: ["decks"] });
          // Redirect to the saved deck page. The "creation is done"
          // signal was invisible when we lingered on /build; users
          // couldn't tell the build had actually finished vs still
          // streaming. Landing on /decks/:slug is the unambiguous
          // finish line.
          if (msg.data.saved_id && myUsername) {
            setSavedDeckId(msg.data.saved_id);
            navigate(`/decks/${myUsername}/${msg.data.saved_id}`);
          }
        } else if (msg.kind === "run_error") {
          setRunning(false);
          setError(msg.data.error);
          refreshBalance();
          queryClient.invalidateQueries({ queryKey: ["builds", "in-progress"] });
        }
      });
      disposeRef.current = dispose;
    },
    [navigate, refreshBalance, queryClient, myUsername],
  );

  // If the page opens with ?resume=<build_id>, attach to it instead
  // of waiting for a new submission. Consume the param once.
  useEffect(() => {
    if (!resumeBuildId) return;
    subscribe(resumeBuildId);
    const next = new URLSearchParams(searchParams);
    next.delete("resume");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clean up the EventSource on unmount.
  useEffect(() => {
    return () => {
      disposeRef.current?.();
    };
  }, []);

  // Scroll only after the validated final payload contains a deck.
  // Once-per-run; reset() clears the guard for the next build.
  useEffect(() => {
    if (!progressive.hasDeck) return;
    if (didScrollToFinalRef.current) return;
    if (!finalResponseRef.current) return;
    didScrollToFinalRef.current = true;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    finalResponseRef.current.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }, [progressive.hasDeck]);

  const onSubmit = useCallback(async () => {
    if (!canSubmit) return;
    try {
      const { build_id } = await createBuild({
        brief: brief.trim(),
        exemplarMode,
        save: true,
        progressive_discovery: progressiveDiscovery,
        // Server validates subscription state and silently downgrades
        // non-subscribers, so the field is safe to send unconditionally.
        // Sending it only when the user actually opted into Opus keeps
        // the request body minimal in the common case.
        ...(isSubscriber && modelPreference === "opus"
          ? { model_preference: "opus" as const }
          : {}),
        ...(images.length > 0
          ? {
              images: images.map((img) => ({
                media_type: img.media_type,
                data_b64: img.data_b64,
              })),
            }
          : {}),
        ...(collectionId ? { collection_id: collectionId } : {}),
      });
      // Invalidate so the /decks "In progress" list picks up the new
      // row immediately instead of waiting for its 8s poll interval.
      queryClient.invalidateQueries({ queryKey: ["builds", "in-progress"] });
      subscribe(build_id);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [
    brief,
    canSubmit,
    exemplarMode,
    progressiveDiscovery,
    subscribe,
    queryClient,
    isSubscriber,
    modelPreference,
    images,
    collectionId,
  ]);

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-8">
      <header>
        <div className="eyebrow">Build</div>
        <h1 className="mt-2 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
          Describe what you want.
          <br />
          <span className="foil">The Oracle drafts the list.</span>
        </h1>
        <p className="text-sm text-fg-muted mt-5 max-w-xl leading-relaxed">
          Ask for a win condition, a budget, a matchup, an archetype. The agent
          drafts a 60-card mainboard and 15-card sideboard live — classifier
          decisions, tool calls, and intermediate reasoning stream as it works.
        </p>
      </header>

      <section className="surface p-5 space-y-4">
        <textarea
          className="input min-h-[120px] font-sans text-[15px] leading-relaxed resize-y"
          placeholder="e.g. Boros prowess aggro with a burn sideboard, budget under $50. Paste a screenshot for inspiration."
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              onSubmit();
            }
          }}
          onPaste={onPaste}
          disabled={running}
        />

        {(images.length > 0 || imageError) && (
          <div className="flex flex-wrap items-start gap-2">
            {images.map((img) => (
              <div
                key={img.id}
                className="relative h-20 w-20 rounded-md overflow-hidden border border-border bg-bg-raised group"
                title={`${img.filename} · ${(img.bytes / 1024).toFixed(0)} KB`}
              >
                <img
                  src={img.preview_url}
                  alt={img.filename}
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeImage(img.id)}
                  disabled={running}
                  className="absolute top-1 right-1 rounded-full bg-black/70 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                  aria-label={`Remove ${img.filename}`}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {imageError && (
              <div className="text-xs text-danger self-center">{imageError}</div>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_IMAGE_TYPES.join(",")}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) {
              void addFiles(e.target.files);
              // Reset so selecting the same file twice still fires onChange.
              e.target.value = "";
            }
          }}
        />

        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4 text-xs">
          <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-1.5">
              <label className="text-fg-muted">Corpus grounding</label>
              <InfoTooltip
                label="What does Corpus grounding do?"
                content={
                  "Grounds the build in the ingested corpus of recent tournament decks — both the exemplar decks the agent synthesizes from AND the meta-archetype snapshot it uses to pick a direction.\n" +
                  "• Auto: a fast classifier decides per-brief — exemplars on for archetype/matchup briefs, off for open-ended creative ones. Meta stays available unless the corpus is stale.\n" +
                  "• On: always use exemplars + meta. Best for grounded, meta-aware builds.\n" +
                  "• Off: skip BOTH exemplar retrieval and meta grounding entirely. The agent builds from its own card evaluation and first principles — use this for brand-new sets or when you want it to ignore the current meta.\n" +
                  "Note: meta grounding is also auto-disabled when the corpus doesn't yet cover the newest released set (the few weeks after a set drops)."
                }
              />
            </div>
            <div className="flex rounded-md border border-border overflow-hidden">
              {(["auto", "on", "off"] as ExemplarMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  disabled={running}
                  onClick={() => setExemplarMode(m)}
                  className={cn(
                    "px-2.5 py-1 text-xs capitalize transition-colors",
                    exemplarMode === m
                      ? "bg-accent/20 text-accent"
                      : "text-fg-muted hover:bg-bg-raised",
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
            <div className="flex items-center gap-1.5">
              <label className="text-fg-muted">Discovery</label>
              <InfoTooltip
                label="What does Discovery do?"
                content={
                  "Progressive discovery: before building, the agent asks 1–3 short clarifying questions about your brief.\n" +
                  "• On: agent pauses to refine the brief — better for vague or ambitious ideas.\n" +
                  "• Off: agent builds straight from your brief — faster, best when you already know what you want."
                }
              />
            </div>
            <div className="flex rounded-md border border-border overflow-hidden">
              {([["on", true], ["off", false]] as Array<[string, boolean]>).map(
                ([label, value]) => (
                  <button
                    key={label}
                    type="button"
                    disabled={running}
                    onClick={() => {
                      setProgressiveDiscovery(value);
                      writePdPref(value);
                    }}
                    className={cn(
                      "px-2.5 py-1 text-xs capitalize transition-colors",
                      progressiveDiscovery === value
                        ? "bg-accent/20 text-accent"
                        : "text-fg-muted hover:bg-bg-raised",
                    )}
                  >
                    {label}
                  </button>
                ),
              )}
            </div>
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
            <CollectionPicker
              value={collectionId}
              onChange={setCollectionId}
              disabled={running}
            />
          </div>
          {isSubscriber && (
            <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto">
              <div className="flex items-center gap-1.5">
                <label className="text-fg-muted">Model</label>
                <InfoTooltip
                  label="What does Model do?"
                  content={
                    "Subscriber perk: pick the builder model.\n" +
                    "• Sonnet: faster and ~5x cheaper — the default for everyone.\n" +
                    "• Opus: slower with deeper analysis — best for hard, ambitious, or contrarian briefs.\n" +
                    "Only subscribers can switch models."
                  }
                />
              </div>
              <div className="flex rounded-md border border-border overflow-hidden">
                {(["sonnet", "opus"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    disabled={running}
                    onClick={() => setModelPreference(m)}
                    className={cn(
                      "px-2.5 py-1 text-xs capitalize transition-colors",
                      modelPreference === m
                        ? "bg-accent/20 text-accent"
                        : "text-fg-muted hover:bg-bg-raised",
                    )}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Primary CTA on its own row, centered. Was previously inline
            with the settings band, but as we've grown the band
            (Exemplars · Discovery · Collection · Model) the button kept
            getting bumped into a single-column tail and reading as
            misaligned. Pulling it out into a dedicated row keeps it the
            visual anchor of the form at any width. */}
        <div className="flex justify-center pt-1">
          <LiquidButton
            className={cn("h-10 text-sm", running && !pendingQuestion && "build-button-working")}
            aria-busy={running && !pendingQuestion}
            disabled={!canSubmit}
            onClick={onSubmit}
            trailing={
              <span className="text-[10px] font-mono opacity-70">
                · {ACTION_COSTS.build_deck}
              </span>
            }
          >
            {running && !pendingQuestion ? <LoaderCircle size={14} strokeWidth={2} className="build-spinner" /> : <Send size={14} strokeWidth={2} />}
            {pendingQuestion ? "Awaiting your answer" : running ? "Building…" : "Build deck"}
          </LiquidButton>
        </div>

        {!running && events.length === 0 && (
          <details className="pt-2 group">
            <summary className="list-none cursor-pointer inline-flex items-center gap-1.5 text-xs text-fg-subtle hover:text-fg-muted transition-colors select-none">
              <span className="font-mono text-fg-subtle group-open:rotate-90 transition-transform inline-block w-2.5">
                ›
              </span>
              Need an example?
            </summary>
            <div className="flex flex-wrap gap-2 pt-2.5">
              {SAMPLE_BRIEFS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setBrief(s)}
                  className="rounded-full border border-border bg-bg-raised/50 hover:bg-bg-raised px-3 py-1 text-xs text-fg-muted hover:text-fg transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </details>
        )}
      </section>

      {activeBuildId && pendingQuestion && (
        <AskUserCard
          buildId={activeBuildId}
          question={pendingQuestion.question}
          context={pendingQuestion.context}
          options={pendingQuestion.options}
          onAnswered={() => setPendingQuestion(null)}
        />
      )}

      {progressive.hasDeck && (
        <div ref={finalResponseRef} style={{ scrollMarginTop: "1rem" }}>
          <StreamingDeckResult
            buffer={finalAnswer ?? ""}
            complete
            meta={streamMetaQuery.data}
            savedDeckId={savedDeckId}
          />
        </div>
      )}

      {(events.length > 0 || running) && (
        <StreamingOutput
          events={events}
          running={running}
          lastText={finalAnswer === null ? lastText : undefined}
          progressiveDiscovery={progressiveDiscovery}
        />
      )}

      {error && (
        <div className="surface p-4 border-danger/40 bg-danger/10 text-sm text-danger">
          {error}
        </div>
      )}
    </div>
  );
}
